import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookHeart, Clock, Search, X } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ARTICLES, findArticle } from "@/data/articles";
import { useHelpedGuides } from "@/hooks/useHelpedGuides";
import { TOPIC_CHIPS, searchArticles } from "@/data/articleTopics";

const SearchBar = ({ q, setQ }) => (
  <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-6" data-testid="articles-search">
    <div className="relative max-w-xl">
      <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mm-muted pointer-events-none" />
      <input className="mm-input !pl-11 !pr-10" type="text" inputMode="search" enterKeyHint="search" placeholder="Search guides, e.g. sleep, grief, panic" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search guides" data-testid="articles-search-input" />
      {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-mm-muted hover:text-mm-primary" aria-label="Clear search" data-testid="articles-search-clear"><X size={16} /></button>}
    </div>
    <div className="flex flex-wrap gap-2 mt-3">
      {TOPIC_CHIPS.map((t) => {
        const on = q.toLowerCase() === t.toLowerCase();
        return <button key={t} onClick={() => setQ(on ? "" : t)} className={`mm-chip !text-sm ${on ? "mm-chip-on" : ""}`} data-testid={`articles-topic-${t.toLowerCase().replace(/ /g, "-")}`}>{t}</button>;
      })}
    </div>
  </section>
);

const ReadAgain = ({ list }) => {
  const items = list.map((g) => findArticle(g.slug)).filter(Boolean).slice(0, 4);
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-8" data-testid="articles-read-again">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-bold text-lg flex items-center gap-2"><BookHeart size={18} className="text-mm-primary" />Read again</h2>
        <Link to="/guides" className="text-sm text-mm-primary font-medium" data-testid="articles-guides-link">Guides you've read ({list.length}) →</Link>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((a) => <Link key={a.slug} to={`/articles/${a.slug}`} className="mm-chip !text-sm" data-testid={`articles-read-again-${a.slug}`}>{a.title}</Link>)}
      </div>
    </section>
  );
};

export default function Articles() {
  const { list } = useHelpedGuides();
  const [q, setQ] = useState("");
  const shown = useMemo(() => searchArticles(ARTICLES, q), [q]);
  usePageMeta({
    title: "Mental Health Articles & Coping Guides | Mindful Me",
    description: "Gentle, practical guides for anxiety, breathing, journaling, safety planning, emotions and supporting the people you love.",
    path: "/articles",
  });
  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-6 mm-fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-mm-primary font-bold">Articles</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3 max-w-3xl">Gentle guides for hard moments</h1>
        <p className="text-mm-muted mt-4 max-w-2xl text-base">Practical, kind skills you can try today: for anxiety, stress, tough days and the people you love.</p>
      </section>
      <SearchBar q={q} setQ={setQ} />
      {list?.length > 0 && !q && <ReadAgain list={list} />}
      {q && <p className="max-w-6xl mx-auto px-5 sm:px-8 pb-4 text-sm text-mm-muted" data-testid="articles-search-count">{shown.length ? `${shown.length} guide${shown.length === 1 ? "" : "s"} for “${q}”` : ""}</p>}
      {q && !shown.length && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-10" data-testid="articles-search-empty">
          <div className="mm-card p-8 text-center"><div className="font-semibold">No guides match “{q}” yet</div><p className="text-sm text-mm-muted mt-1">Try a broader word like anxiety, sleep or stress.</p></div>
        </div>
      )}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mm-stagger" data-testid="articles-list">
        {shown.map((a) => (
          <Link key={a.slug} to={`/articles/${a.slug}`} className="mm-card overflow-hidden group transition-transform hover:-translate-y-1" data-testid={`article-card-${a.slug}`}>
            <div className="aspect-[16/10] overflow-hidden bg-mm-surface-light">
              <img src={a.image} alt={a.imageAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-5">
              <div className="text-xs text-mm-muted inline-flex items-center gap-1"><Clock size={12} /> {a.readMins} min read</div>
              <h2 className="text-lg font-bold mt-1.5 leading-snug group-hover:text-mm-primary transition-colors">{a.title}</h2>
              <p className="text-sm text-mm-muted mt-2 line-clamp-3">{a.description}</p>
            </div>
          </Link>
        ))}
      </section>
    </PublicLayout>
  );
}
